/**
 * FormPageIndicator
 * A paginated public form's progress: one dot per page (the current one widened in the page
 * color, visited ones clickable to jump back) and a "Page X of Y" label. Shared by FormModal and
 * a combined form + hotel CTA, whose booking shows as the form's last page.
 */

interface FormPageIndicatorProps {
  /** 0-based index of the current page. */
  currentPage: number;
  totalPages: number;
  primaryColor: string;
  /** Word for a page ("Page", or "Step" outside a multi-step CTA). */
  label: "Page" | "Step";
  /** Called with a visited page's index when its dot is clicked. */
  onPageClick: (page: number) => void;
}

/**
 * Renders the page dots and label.
 * @param props - See {@link FormPageIndicatorProps}
 * @returns The indicator
 */
export function FormPageIndicator({
  currentPage,
  totalPages,
  primaryColor,
  label,
  onPageClick,
}: FormPageIndicatorProps): JSX.Element {
  return (
    <div className="mb-6 flex items-center gap-3">
      <div className="flex items-center">
        {Array.from({ length: totalPages }).map((_, i) => {
          const isActive = i === currentPage;
          const isVisited = i < currentPage;
          return (
            <button
              key={i}
              type="button"
              onClick={() => onPageClick(i)}
              disabled={!isVisited}
              aria-label={`Go to ${label.toLowerCase()} ${i + 1}`}
              aria-current={isActive ? "step" : undefined}
              className="group flex h-9 touch-manipulation items-center justify-center px-1 focus:outline-none focus-visible:rounded focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-gray-400 disabled:cursor-default"
            >
              <span
                className={`block h-1.5 rounded-full transition-all ${
                  isActive
                    ? "w-6"
                    : `w-1.5 bg-gray-200 ${isVisited ? "[@media(hover:hover)]:group-hover:bg-gray-300" : ""}`
                }`}
                style={isActive ? { backgroundColor: primaryColor } : undefined}
              />
            </button>
          );
        })}
      </div>
      <span className="text-sm font-medium text-gray-500">
        {label} {currentPage + 1} of {totalPages}
      </span>
    </div>
  );
}
